import {
  CONTENT_PAGE_SEO,
  CONTENT_PAGE_SLUG,
  CONTENT_PAGE_LIMITS,
  parseInline,
  parseRichText,
  plainText,
  safeHref,
  type ContentPageStatus,
  type RichTextInline,
} from '@ecom/contracts';
import type { MessageKey } from '@/i18n/messages';

/**
 * The content pages' editor, as plain functions (ADR-058): a page read from
 * and written back to `pages.content`, what its words add up to, how it will
 * read in a search result, and the edits the editor's toolbar makes to the
 * text. Nothing here draws anything, so all of it is tested on its own.
 */

export type Language = 'ar' | 'en';
export const LANGUAGES: readonly Language[] = ['ar', 'en'];

export interface PageText {
  title: string;
  body: string;
  metaTitle: string;
  metaDescription: string;
}

/** One page as the editor holds it — every field present, so a form never reads `undefined`. */
export interface PageDraft {
  ar: PageText;
  en: PageText;
  status: ContentPageStatus;
  noindex: boolean;
  footer: boolean;
  /** `{ key, url }` from an upload, a path, or nothing. */
  image: unknown;
  updatedAt: string | null;
}

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

function readText(value: unknown): PageText {
  const record = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  return {
    title: str(record.title),
    body: str(record.body),
    metaTitle: str(record.metaTitle),
    metaDescription: str(record.metaDescription),
  };
}

export function readPage(value: unknown): PageDraft {
  const record = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  return {
    ar: readText(record.ar),
    en: readText(record.en),
    // A page saved before drafts existed was live: it stays live.
    status: record.status === 'draft' ? 'draft' : 'published',
    noindex: record.noindex === true,
    footer: record.footer !== false,
    image: record.image ?? null,
    updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : null,
  };
}

/** A page as the setting stores it: empty search fields left out, so the storefront's fallbacks apply. */
export function writePage(page: PageDraft): Record<string, unknown> {
  const text = (value: PageText) => ({
    title: value.title,
    body: value.body,
    ...(value.metaTitle.trim() ? { metaTitle: value.metaTitle } : {}),
    ...(value.metaDescription.trim() ? { metaDescription: value.metaDescription } : {}),
  });
  return {
    ar: text(page.ar),
    en: text(page.en),
    status: page.status,
    noindex: page.noindex,
    footer: page.footer,
    ...(page.image ? { image: page.image } : {}),
    ...(page.updatedAt ? { updatedAt: page.updatedAt } : {}),
  };
}

export function emptyPage(): PageDraft {
  return readPage({ status: 'draft' });
}

/** Whether a language has the two things a page needs to show: a title and a body. */
export function isWritten(text: PageText): boolean {
  return Boolean(text.title.trim() && text.body.trim());
}

/** Where a page stands, for the list: live, a draft, or not written in either language. */
export function pageState(page: PageDraft): 'published' | 'draft' | 'empty' {
  if (!isWritten(page.ar) && !isWritten(page.en)) return 'empty';
  return page.status;
}

// ─── Addresses ───────────────────────────────────────────────────────────────

/** An address from a title — Latin letters and digits only, so an Arabic-only title gives nothing to suggest. */
export function slugify(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, CONTENT_PAGE_LIMITS.slug)
    .replace(/-+$/, '');
}

/** Why an address cannot be used, or null. */
export function slugProblem(slug: string, taken: readonly string[]): MessageKey | null {
  if (!slug) return 'pageSlugRequired';
  if (slug.length > CONTENT_PAGE_LIMITS.slug || !CONTENT_PAGE_SLUG.test(slug)) return 'pageSlugInvalid';
  if (taken.includes(slug)) return 'pageSlugTaken';
  return null;
}

// ─── What the words add up to ────────────────────────────────────────────────

const words = (parts: RichTextInline[]) => parts.map((part) => part.text).join('');

export interface TextStats {
  words: number;
  /** Minutes at 200 words a minute, at least one for any text. */
  minutes: number;
  sections: number;
  links: number;
  internalLinks: number;
  /** Links the storefront would drop, keeping only their words. */
  brokenLinks: string[];
  outline: { level: 2 | 3; text: string; line: number }[];
}

const LINK = /\[([^\]\n]+)\]\((\S+?)\)/g;

export function textStats(body: string): TextStats {
  const blocks = parseRichText(body);
  const count = plainText(body).split(/\s+/).filter(Boolean).length;
  const hrefs = [...body.matchAll(LINK)].map((match) => match[2]);
  const safe = hrefs.filter((href) => safeHref(href) !== null);
  const outline: TextStats['outline'] = [];
  body.split('\n').forEach((raw, line) => {
    const match = /^\s*(#{1,3})\s+(.+)$/.exec(raw);
    if (match) outline.push({ level: match[1].length === 3 ? 3 : 2, text: words(parseInline(match[2].trim())), line });
  });
  return {
    words: count,
    minutes: count === 0 ? 0 : Math.max(1, Math.round(count / 200)),
    sections: blocks.filter((block) => block.type === 'heading' && block.level === 2).length,
    links: safe.length,
    internalLinks: safe.filter((href) => href.startsWith('/') || href.startsWith('#')).length,
    brokenLinks: hrefs.filter((href) => safeHref(href) === null),
    outline,
  };
}

/** The opening words, as the storefront would write a description nobody wrote (lib/seo.ts). */
export function autoDescription(body: string, limit: number = CONTENT_PAGE_SEO.description.max - 5): string {
  const flat = plainText(body).replace(/\s+/g, ' ').trim();
  if (flat.length <= limit) return flat;
  const cut = flat.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

// ─── Search ──────────────────────────────────────────────────────────────────

export type CheckLevel = 'good' | 'warn' | 'bad';

export interface SeoCheck {
  id: string;
  level: CheckLevel;
  message: MessageKey;
  values?: Record<string, string | number>;
}

/**
 * What a page looks like to a search engine in one language — each check a
 * plain sentence an operator can act on, worst first. Measured on what the
 * storefront will actually write: the store's name after the title, the
 * opening words where no description was written.
 */
export function seoChecks(page: PageDraft, language: Language, storeName: string): SeoCheck[] {
  const text = page[language];
  const other = page[language === 'ar' ? 'en' : 'ar'];
  const stats = textStats(text.body);
  const checks: SeoCheck[] = [];
  const add = (id: string, level: CheckLevel, message: MessageKey, values?: SeoCheck['values']) =>
    checks.push({ id, level, message, values });

  if (!isWritten(text)) add('written', 'bad', 'seoNotWritten');

  const title = searchTitle(text, storeName);
  const { min: tMin, max: tMax } = CONTENT_PAGE_SEO.title;
  if (!title.trim()) add('title', 'bad', 'seoTitleMissing');
  else if (title.length > tMax) add('title', 'warn', 'seoTitleLong', { count: title.length, max: tMax });
  else if (title.length < tMin) add('title', 'warn', 'seoTitleShort', { count: title.length, min: tMin });
  else add('title', 'good', 'seoTitleGood', { count: title.length });

  const description = text.metaDescription.trim();
  const { min: dMin, max: dMax } = CONTENT_PAGE_SEO.description;
  if (!description) add('description', text.body.trim() ? 'warn' : 'bad', 'seoDescriptionAuto');
  else if (description.length > dMax) add('description', 'warn', 'seoDescriptionLong', { count: description.length, max: dMax });
  else if (description.length < dMin) add('description', 'warn', 'seoDescriptionShort', { count: description.length, min: dMin });
  else add('description', 'good', 'seoDescriptionGood', { count: description.length });

  if (stats.words >= CONTENT_PAGE_SEO.words) add('length', 'good', 'seoLengthGood', { count: stats.words });
  else add('length', 'warn', 'seoLengthShort', { count: stats.words, min: CONTENT_PAGE_SEO.words });

  if (stats.sections > 0) add('sections', 'good', 'seoSectionsGood', { count: stats.sections });
  else if (stats.words > 150) add('sections', 'warn', 'seoSectionsMissing');

  const firstHeading = stats.outline[0];
  if (firstHeading && firstHeading.level === 3) add('order', 'warn', 'seoHeadingOrder');

  if (stats.brokenLinks.length > 0) add('links', 'bad', 'seoLinksBroken', { link: stats.brokenLinks[0] });
  else if (stats.internalLinks > 0) add('links', 'good', 'seoLinksGood', { count: stats.internalLinks });
  else if (text.body.trim()) add('links', 'warn', 'seoLinksMissing');

  if (!isWritten(other)) add('other', 'bad', language === 'ar' ? 'seoEnglishMissing' : 'seoArabicMissing');

  if (page.noindex) add('index', 'warn', 'seoHidden');
  if (page.image) add('image', 'good', 'seoImageGood');
  else add('image', 'warn', 'seoImageMissing');

  const order: Record<CheckLevel, number> = { bad: 0, warn: 1, good: 2 };
  return checks.sort((a, b) => order[a.level] - order[b.level]);
}

/** Out of 100: a passed check counts whole, a warning half, a failure nothing. */
export function seoScore(checks: SeoCheck[]): number {
  if (checks.length === 0) return 0;
  const points = checks.reduce((sum, check) => sum + (check.level === 'good' ? 1 : check.level === 'warn' ? 0.5 : 0), 0);
  return Math.round((points / checks.length) * 100);
}

/** The title a search result shows: the store's own, or the page's, framed with the store's name as the storefront does. */
export function searchTitle(text: PageText, storeName: string): string {
  const own = text.metaTitle.trim() || text.title.trim();
  if (!own) return '';
  return storeName ? `${own} · ${storeName}` : own;
}

// ─── The toolbar's edits ─────────────────────────────────────────────────────

/** The text and what is selected in it — the editor's state at the moment of an edit. */
export interface TextSelection {
  value: string;
  start: number;
  end: number;
}

export type LineStyle = 'h2' | 'h3' | 'bullet' | 'number';

const LINE_PREFIX: Record<LineStyle, string> = { h2: '## ', h3: '### ', bullet: '- ', number: '1. ' };
const ANY_PREFIX = /^(#{1,3}\s+|[-•*]\s+|[0-9٠-٩]{1,3}[.)]\s+)/;

/** The whole lines a selection touches. */
function lineRange(value: string, start: number, end: number): [number, number] {
  const from = value.lastIndexOf('\n', start - 1) + 1;
  const newline = value.indexOf('\n', end > start && value[end - 1] === '\n' ? end - 1 : end);
  return [from, newline === -1 ? value.length : newline];
}

/**
 * Makes the selected lines a heading or a list — or plain again if they
 * already were that. A numbered list counts up from one.
 */
export function toggleLineStyle(selection: TextSelection, style: LineStyle): TextSelection {
  const { value, start, end } = selection;
  const [from, to] = lineRange(value, start, end);
  const lines = value.slice(from, to).split('\n');
  const prefix = LINE_PREFIX[style];
  const has = (line: string) =>
    style === 'number' ? /^[0-9٠-٩]{1,3}[.)]\s+/.test(line) : style === 'bullet' ? /^[-•*]\s+/.test(line) : line.startsWith(prefix) && !line.startsWith(`${prefix}#`);
  const already = lines.filter((line) => line.trim()).every(has);
  let n = 0;
  const next = lines
    .map((line) => {
      if (!line.trim()) return line;
      const bare = line.replace(ANY_PREFIX, '');
      if (already) return bare;
      n += 1;
      return `${style === 'number' ? `${n}. ` : prefix}${bare}`;
    })
    .join('\n');
  const result = value.slice(0, from) + next + value.slice(to);
  return { value: result, start: from, end: from + next.length };
}

/** Wraps the selection in `**` — or unwraps it — selecting the words; with nothing selected, places the cursor between. */
export function toggleBold(selection: TextSelection, placeholder: string): TextSelection {
  const { value, start, end } = selection;
  const before = value.slice(0, start);
  const after = value.slice(end);
  if (before.endsWith('**') && after.startsWith('**')) {
    return { value: before.slice(0, -2) + value.slice(start, end) + after.slice(2), start: start - 2, end: end - 2 };
  }
  const chosen = value.slice(start, end);
  const text = chosen || placeholder;
  return { value: `${before}**${text}**${after}`, start: start + 2, end: start + 2 + text.length };
}

/**
 * Writes `[text](href)` over the selection. Bold around it is taken off: the
 * formatting has no bold link, and `**[a](b)**` would show its brackets.
 */
export function insertLink(selection: TextSelection, text: string, href: string): TextSelection {
  const { value } = selection;
  let { start, end } = selection;
  if (value.slice(0, start).endsWith('**') && value.slice(end).startsWith('**')) {
    start -= 2;
    end += 2;
  }
  const link = `[${text.replace(/[[\]\n]/g, '')}](${href.trim()})`;
  return { value: value.slice(0, start) + link + value.slice(end), start: start + link.length, end: start + link.length };
}

/**
 * Enter inside a list: the next line carries on the list, numbered one on.
 * Enter on an empty item ends the list. Null where Enter is just a new line.
 */
export function continueList(selection: TextSelection): TextSelection | null {
  const { value, start, end } = selection;
  if (start !== end) return null;
  const from = value.lastIndexOf('\n', start - 1) + 1;
  const line = value.slice(from, start);
  const match = /^(\s*)([-•*]|([0-9]{1,3})([.)]))\s+(.*)$/.exec(line);
  if (!match) return null;
  if (!match[5].trim()) {
    // An empty item: the list ends here and the line is cleared.
    return { value: value.slice(0, from) + value.slice(start), start: from, end: from };
  }
  const marker = match[3] ? `${Number(match[3]) + 1}${match[4]}` : match[2];
  const insert = `\n${match[1]}${marker} `;
  return { value: value.slice(0, start) + insert + value.slice(end), start: start + insert.length, end: start + insert.length };
}

/** The style of the line the cursor is on — what the toolbar shows as pressed. */
export function lineStyleAt(value: string, position: number): LineStyle | null {
  const from = value.lastIndexOf('\n', position - 1) + 1;
  const newline = value.indexOf('\n', position);
  const line = value.slice(from, newline === -1 ? value.length : newline);
  if (/^###\s/.test(line)) return 'h3';
  if (/^#{1,2}\s/.test(line)) return 'h2';
  if (/^[-•*]\s/.test(line)) return 'bullet';
  if (/^[0-9٠-٩]{1,3}[.)]\s/.test(line)) return 'number';
  return null;
}

// ─── Pasting from a document ─────────────────────────────────────────────────

/**
 * What was copied from Word or Google Docs, in the pages' formatting: its
 * headings, lists, bold and links kept, everything else as plain words. The
 * HTML is read, never inserted — the body stays text (ADR-058).
 */
export function htmlToRichText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out: string[] = [];

  const inline = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/\s+/g, ' ');
    if (!(node instanceof HTMLElement)) return '';
    const inner = [...node.childNodes].map(inline).join('');
    const tag = node.tagName.toLowerCase();
    const weight = node.style.fontWeight;
    if (tag === 'br') return '\n';
    if ((tag === 'strong' || tag === 'b' || weight === 'bold' || Number(weight) >= 600) && inner.trim()) {
      // Google Docs wraps a whole document in <b style="font-weight:normal">.
      if (weight === 'normal' || weight === '400') return inner;
      return `**${inner.trim()}**${/\s$/.test(inner) ? ' ' : ''}`;
    }
    if (tag === 'a') {
      const href = node.getAttribute('href') ?? '';
      return safeHref(href) && inner.trim() ? `[${inner.trim()}](${href})` : inner;
    }
    return inner;
  };

  const block = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').trim();
      if (text) out.push(text);
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    const tag = node.tagName.toLowerCase();
    if (/^h[1-2]$/.test(tag)) out.push(`## ${inline(node).trim()}`);
    else if (/^h[3-6]$/.test(tag)) out.push(`### ${inline(node).trim()}`);
    else if (tag === 'ul' || tag === 'ol') {
      let n = 0;
      const items = [...node.children].filter((child) => child.tagName.toLowerCase() === 'li');
      out.push(items.map((item) => `${tag === 'ol' ? `${(n += 1)}.` : '-'} ${inline(item).trim()}`).join('\n'));
    } else if (tag === 'p' || tag === 'li' || tag === 'blockquote') {
      const text = inline(node).trim();
      if (text) out.push(text);
    } else if (['script', 'style', 'meta', 'title', 'head'].includes(tag)) {
      return;
    } else {
      [...node.childNodes].forEach(block);
    }
  };

  [...doc.body.childNodes].forEach(block);
  return out
    .map((part) => part.replace(/[ \t]+\n/g, '\n').trim())
    .filter(Boolean)
    .join('\n\n');
}
